import { ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DEFAULT_THUMB, type Theme } from '../themes';
import { themeAssetUrl } from '../themeAssets';

interface ThemeThumbnailProps {
  theme: Theme;
  className?: string;
}

/**
 * A single theme's preview swatch. Reuses the full `tN.webp` scaled down by CSS
 * (no separate thumbnail file) and lazy-loads it, so tiles only fetch as they're
 * shown. Three states:
 *  - imageless default → its clean gradient;
 *  - declared theme whose `tN.webp` isn't in the repo → an explicit icon
 *    placeholder (never a fake photo);
 *  - everything else → the image.
 */
export function ThemeThumbnail({ theme, className }: ThemeThumbnailProps) {
  const url = themeAssetUrl(theme.file);

  if (theme.file == null) {
    return <div className={cn('h-full w-full', className)} style={{ background: DEFAULT_THUMB }} />;
  }

  if (!url) {
    return (
      <div
        className={cn(
          'flex h-full w-full items-center justify-center bg-white/[0.03] text-lo',
          className,
        )}
        title={`${theme.name} — image not added yet`}
      >
        <ImageOff size={14} strokeWidth={1.8} />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={theme.name}
      loading="lazy"
      decoding="async"
      className={cn('h-full w-full object-cover', className)}
    />
  );
}
